/* eslint-disable no-undef */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from "react";
import "./css/StaffPage.css";
import {
  getAllApplications,
  approveApplication,
  rejectApplication,
} from "../api/careerApi";
import {
  FiUsers,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiX,
  FiSearch,
  FiFileText,
  FiPhone,
  FiMail,
  FiExternalLink,
  FiAlertCircle,
} from "react-icons/fi";
import { BsCurrencyRupee } from "react-icons/bs";
import AccessControl from "../components/AccessControl";

const ADMIN_PANEL_ROLES = ["SUPERADMIN", "ADMIN", "HR"];

const STATUS_META = {
  pending: { label: "Pending", color: "#854F0B", bg: "#FAEEDA" },
  approved: { label: "Approved", color: "#0F6E56", bg: "#E1F5EE" },
  rejected: { label: "Rejected", color: "#A32D2D", bg: "#FCEBEB" },
};

export default function JobApplicationsPage() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  // Approve modal
  const [approveModal, setApproveModal] = useState(null); // application object
  const [baseSalary, setBaseSalary] = useState("");
  const [tempPassword, setTempPassword] = useState("password@123");
  const [approving, setApproving] = useState(false);

  // Reject modal
  const [rejectModal, setRejectModal] = useState(null); // application object
  const [reviewNotes, setReviewNotes] = useState("");
  const [rejecting, setRejecting] = useState(false);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  const fetchApplications = useCallback(async (status) => {
    setLoading(true);
    try {
      const res = await getAllApplications(status === "all" ? "" : status);
      if (res.success) {
        setApplications(res.applications || []);
      } else {
        setApplications([]);
        showToast(res.error || "Failed to load applications", "error");
      }
    } catch {
      setApplications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApplications(filterStatus);
  }, [filterStatus, fetchApplications]);

  const stats = {
    total: applications.length,
    pending: applications.filter((a) => a.status === "pending").length,
    approved: applications.filter((a) => a.status === "approved").length,
    rejected: applications.filter((a) => a.status === "rejected").length,
  };

  const filtered = applications.filter((a) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      `${a.firstName} ${a.lastName}`.toLowerCase().includes(q) ||
      a.email?.toLowerCase().includes(q) ||
      a.phone?.toLowerCase().includes(q)
    );
  });

  // ── Approve ──────────────────────────────────────────────────────────
  const openApproveModal = (app) => {
    setApproveModal(app);
    setBaseSalary("");
    setTempPassword("");
  };

  const handleApprove = async () => {
    if (!baseSalary || parseFloat(baseSalary) <= 0) {
      showToast("Enter a valid base salary", "error");
      return;
    }
    setApproving(true);
    try {
      const res = await approveApplication(
        approveModal._id,
        parseFloat(baseSalary),
        tempPassword || undefined
      );
      if (res.success) {
        showToast(`${approveModal.firstName} approved as BDE`);
        setApproveModal(null);
        fetchApplications(filterStatus);
      } else {
        showToast(res.error || res.message || "Approval failed", "error");
      }
    } catch {
      showToast("Approval failed", "error");
    } finally {
      setApproving(false);
    }
  };

  // ── Reject ───────────────────────────────────────────────────────────
  const openRejectModal = (app) => {
    setRejectModal(app);
    setReviewNotes("");
  };

  const handleReject = async () => {
    setRejecting(true);
    try {
      const res = await rejectApplication(rejectModal._id, reviewNotes);
      if (res.success) {
        showToast("Application rejected");
        setRejectModal(null);
        fetchApplications(filterStatus);
      } else {
        showToast(res.error || res.message || "Reject failed", "error");
      }
    } catch {
      showToast("Reject failed", "error");
    } finally {
      setRejecting(false);
    }
  };

  return (
    <div>
      <AccessControl allowedRoles={ADMIN_PANEL_ROLES} redirectTo="/" />

      {toast.show && (
        <div className={`sf-toast sf-toast-${toast.type}`}>
          <span>
            {toast.type === "success" ? (
              <FiCheckCircle size={16} />
            ) : toast.type === "warning" ? (
              <FiAlertCircle size={16} />
            ) : (
              <FiXCircle size={16} />
            )}
          </span>
          <span>{toast.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="sf-page-header section">
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: "#1a1a1a", margin: 0 }}>
            Job Applications
          </h2>
          <p style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
            BDE career applications submitted from the website
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="stat-grid section">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "#E6F1FB" }}>
            <FiUsers size={18} color="#185FA5" />
          </div>
          <div className="stat-label">Total Applications</div>
          <div className="stat-value">{stats.total}</div>
          <div className="stat-change neutral">All time</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "#FAEEDA" }}>
            <FiClock size={18} color="#854F0B" />
          </div>
          <div className="stat-label">Pending Review</div>
          <div className="stat-value" style={{ color: "#854F0B" }}>{stats.pending}</div>
          <div className="stat-change neutral">Awaiting decision</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "#E1F5EE" }}>
            <FiCheckCircle size={18} color="#0F6E56" />
          </div>
          <div className="stat-label">Approved</div>
          <div className="stat-value" style={{ color: "#0F6E56" }}>{stats.approved}</div>
          <div className="stat-change up">Converted to BDE staff</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "#FCEBEB" }}>
            <FiXCircle size={18} color="#A32D2D" />
          </div>
          <div className="stat-label">Rejected</div>
          <div className="stat-value" style={{ color: "#A32D2D" }}>{stats.rejected}</div>
          <div className="stat-change down">Not moving forward</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="card section">
        <div className="sf-toolbar">
          <div className="filter-tabs">
            {["all", "pending", "approved", "rejected"].map((f) => (
              <button
                key={f}
                className={`filter-tab ${filterStatus === f ? "active" : ""}`}
                onClick={() => setFilterStatus(f)}
              >
                {f === "all" ? "All" : STATUS_META[f].label}
              </button>
            ))}
          </div>
          <div className="sf-toolbar-right">
            <div className="search-box">
              <FiSearch size={14} className="search-icon" />
              <input
                placeholder="Search name, email or phone…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="search-clear" onClick={() => setSearchTerm("")}>
                  <FiX size={12} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card section">
        {loading ? (
          <div className="sf-loading" style={{ height: "40vh" }}>
            <div
              className="spinner"
              style={{ width: 24, height: 24, borderColor: "#1d9e75", borderTopColor: "transparent" }}
            ></div>
            <p style={{ marginTop: 12, color: "#888", fontSize: 13 }}>Loading applications…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="sf-empty">
            <FiFileText size={40} color="#ddd" />
            <p>No applications found</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="sf-table">
              <thead>
                <tr>
                  <th>Applicant</th>
                  <th>Contact</th>
                  <th>Applied On</th>
                  <th>Resume</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => {
                  const meta = STATUS_META[a.status] || STATUS_META.pending;
                  return (
                    <tr key={a._id}>
                      <td>
                        <div className="sf-member-cell">
                          <div className="sf-avatar">
                            {a.firstName?.charAt(0)?.toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>
                              {a.firstName} {a.lastName}
                            </div>
                            <div style={{ fontSize: 11, color: "#888" }}>
                              Applied for {a.position || "BDE"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 5, marginBottom: 3 }}>
                          <FiMail size={11} color="#888" /> {a.email}
                        </div>
                        <div style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 5 }}>
                          <FiPhone size={11} color="#888" /> {a.phone}
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: "#555" }}>
                        {a.createdAt ? new Date(a.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                      </td>
                      <td>
                        {a.resume?.url ? (
                          <a
                            href={a.resume.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: 12, color: "#185FA5", display: "flex", alignItems: "center", gap: 4, textDecoration: "none" }}
                          >
                            View <FiExternalLink size={11} />
                          </a>
                        ) : (
                          <span style={{ fontSize: 12, color: "#aaa" }}>—</span>
                        )}
                      </td>
                      <td>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            padding: "4px 10px",
                            borderRadius: 20,
                            color: meta.color,
                            background: meta.bg,
                          }}
                        >
                          {meta.label}
                        </span>
                      </td>
                      <td>
                        {a.status === "pending" ? (
                          <div style={{ display: "flex", gap: 8 }}>
                            <button className="btn btn-success" style={{ padding: "6px 12px", fontSize: 12 }} onClick={() => openApproveModal(a)}>
                              Approve
                            </button>
                            <button className="btn btn-danger" style={{ padding: "6px 12px", fontSize: 12 }} onClick={() => openRejectModal(a)}>
                              Reject
                            </button>
                          </div>
                        ) : a.status === "approved" ? (
                          <span style={{ fontSize: 12, color: "#0F6E56" }}>
                            Staff created {a.offeredBaseSalary ? `· ₹${a.offeredBaseSalary}/mo` : ""}
                          </span>
                        ) : (
                          <span style={{ fontSize: 12, color: "#999" }}>
                            {a.reviewNotes || "—"}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Approve Modal */}
      {approveModal && (
        <div className="modal-overlay" onClick={() => setApproveModal(null)}>
          <div className="modal-content" style={{ maxWidth: 440, height: "auto", minHeight: "auto" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FiCheckCircle size={15} style={{ marginRight: 7 }} /> Approve Application</h3>
              <button className="modal-close" onClick={() => setApproveModal(null)}><FiX size={18} /></button>
            </div>
            <div className="modal-body" style={{ padding: "20px 24px" }}>
              <div style={{ marginBottom: 16, padding: "10px 14px", background: "#f9fafb", borderRadius: 8 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {approveModal.firstName} {approveModal.lastName}
                </div>
                <div style={{ fontSize: 12, color: "#888" }}>{approveModal.email} · {approveModal.phone}</div>
              </div>
              <p style={{ fontSize: 12, color: "#888", marginBottom: 14 }}>
                This creates a BDE staff account, generates a driver-invite code, and emails login details to the applicant.
              </p>

              <label style={{ fontSize: 12, fontWeight: 600, color: "#555", display: "block", marginBottom: 6 }}>
                Base Salary (Monthly) *
              </label>
              <div style={{ position: "relative", marginBottom: 14 }}>
                <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "#888" }}>
                  <BsCurrencyRupee size={13} />
                </span>
                <input
                  type="number"
                  placeholder="e.g. 12000"
                  value={baseSalary}
                  onChange={(e) => setBaseSalary(e.target.value)}
                  style={{ width: "100%", padding: "10px 12px 10px 28px", fontSize: 14, borderRadius: 8, border: "1.5px solid #e5e7eb", boxSizing: "border-box" }}
                  autoFocus
                />
              </div>

              <label style={{ fontSize: 12, fontWeight: 600, color: "#555", display: "block", marginBottom: 6 }}>
                Temporary Password (optional — auto-generated if left blank)
              </label>
              <input
                type="text"
                placeholder="Leave blank to auto-generate"
                value={tempPassword}
                onChange={(e) => setTempPassword(e.target.value)}
                style={{ width: "100%", padding: "10px 12px", fontSize: 14, borderRadius: 8, border: "1.5px solid #e5e7eb", boxSizing: "border-box" }}
              />
            </div>
            <div className="modal-actions">
              <button className="btn-outline-green" onClick={() => setApproveModal(null)}>Cancel</button>
              <button className="btn btn-success" onClick={handleApprove} disabled={approving}>
                {approving ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Approving…</> : <><FiCheckCircle size={13} /> Approve & Create BDE</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div className="modal-overlay" onClick={() => setRejectModal(null)}>
          <div className="modal-content" style={{ maxWidth: 420, height: "auto", minHeight: "auto" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FiXCircle size={15} style={{ marginRight: 7 }} /> Reject Application</h3>
              <button className="modal-close" onClick={() => setRejectModal(null)}><FiX size={18} /></button>
            </div>
            <div className="modal-body" style={{ padding: "20px 24px" }}>
              <div style={{ marginBottom: 16, padding: "10px 14px", background: "#f9fafb", borderRadius: 8 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {rejectModal.firstName} {rejectModal.lastName}
                </div>
                <div style={{ fontSize: 12, color: "#888" }}>{rejectModal.email}</div>
              </div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#555", display: "block", marginBottom: 6 }}>
                Reason (optional — not shown to applicant)
              </label>
              <textarea
                rows={3}
                placeholder="e.g. Not enough field experience"
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                style={{ width: "100%", padding: "10px 12px", fontSize: 14, borderRadius: 8, border: "1.5px solid #e5e7eb", boxSizing: "border-box", resize: "vertical" }}
              />
            </div>
            <div className="modal-actions">
              <button className="btn-outline-green" onClick={() => setRejectModal(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleReject} disabled={rejecting}>
                {rejecting ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Rejecting…</> : <><FiXCircle size={13} /> Reject</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}